import { BlobServiceClient } from '@azure/storage-blob';
import { DataLakeServiceClient } from '@azure/storage-file-datalake';

import {
  azureConnectionString,
  azureStorageAccountName,
  useAzurite,
} from '@/config';
import { IFile } from '@/models';
import { useFileStore, useToastStore, useUserStore } from '@/store';
import {
  generateFileFromBase64,
  generateRandomFileName,
  getFileUrlWithValidToken,
  readFile,
} from '@/utils';

const uploadFolder = 'tmp';

export const getFolderNameFromUrl = (fileUrl?: string): string | undefined => {
  // Get folder name from azure blob storage URL.
  if (!fileUrl) return undefined;

  const parsedUrl = new URL(fileUrl);
  const pathname = parsedUrl.pathname;

  if (!pathname) return undefined;
  return pathname.split('/')[2];
};

export const getSasTokenFromFileUrl = async (
  fileUrl?: string,
): Promise<string | undefined> => {
  // Get SAS token matched with folder name from file URL.
  const folderName = getFolderNameFromUrl(fileUrl);
  if (!folderName) return undefined;
  const { fileTokens, checkUploadTokenExpiration, fetchFileTokens } =
    useFileStore.getState();

  const isTokenExpired = !fileTokens || checkUploadTokenExpiration(folderName);

  if (isTokenExpired) {
    const newTokens = await fetchFileTokens();
    return newTokens[folderName]?.sasToken;
  } else {
    return fileTokens[folderName]?.sasToken;
  }
};

export const uploadFiles = async (
  files: IFile[],
  fileNamePrefix?: string,
): Promise<IFile[]> => {
  const { account, user } = useUserStore.getState();

  if (!account?._id || !user?._id) {
    throw new Error(
      'User or account information is undefined, please log in again.',
    );
  }

  // Nothing new to upload (e.g. saving a record whose files are all already
  // stored): skip all storage work, including the container/directory create
  // that Azure answers with a benign 409 (PathAlreadyExists) in the Network tab.
  if (!files.some((fileItem) => fileItem.file)) {
    return files;
  }

  // if use_azurite set azurite local storage url
  const azureStorageUrl: string =
    useAzurite === 'true'
      ? `http://127.0.0.1:10000/${azureStorageAccountName}`
      : `https://${azureStorageAccountName}.blob.core.windows.net`;
  const containerName = `acct-${account._id}`;
  const directoryName = `${uploadFolder}/${user._id}`;

  // if azure connection string is set, upload files to azure storage
  if (azureConnectionString) {
    const blobServiceClient = BlobServiceClient.fromConnectionString(
      azureConnectionString,
    );

    const containerClient = blobServiceClient.getContainerClient(containerName);
    // create container if not exist
    try {
      // exists() (a HEAD) instead of createIfNotExists(): the latter issues the
      // create unconditionally and Azure returns a 409 for an existing
      // container, which surfaces as a confusing error in the Network tab.
      if (!(await containerClient.exists())) {
        await containerClient.create();
      }
    } catch (error: any) {
      const statusCode = error?.statusCode || error?.response?.status;
      const errorCode =
        error?.code || error?.details?.errorCode || error?.response?.errorCode;
      if (
        statusCode !== 409 &&
        errorCode !== 'ContainerAlreadyExists' &&
        errorCode !== 'PathAlreadyExists'
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          'File Debug: Re-creating container - ',
          error?.message || error,
        );
      }
    }

    return Promise.all(
      files.map(async (fileItem) => {
        const file: File | undefined = fileItem.file;
        if (!file) return fileItem;

        let fileName = generateRandomFileName(file.name);
        if (fileNamePrefix) {
          fileName = `${fileNamePrefix}-${fileName}`;
        }

        const blobDirectory = directoryName
          ? `${directoryName}/${fileName}`
          : fileName;
        const fileClient = containerClient.getBlockBlobClient(blobDirectory);

        // Upload the file
        const fileBuffer = await readFile(file);
        const uploadResponse = await fileClient.uploadData(
          fileBuffer as ArrayBuffer,
          {
            blobHTTPHeaders: { blobContentType: file.type },
            maxSingleShotSize: 15 * 1024 * 1024, // 15 MB
          },
        );
        if (uploadResponse) {
          // e.g. http://127.0.0.1:10000/devstoreaccount1/acct-6718d097e55d072a4d8245e4/tmp/6718d137e55d072a4d824614/1738611738112-czcmhwx.pdf
          const { file: _file, ...rest } = fileItem as any;
          return { ...rest, url: fileClient.url };
        }

        return fileItem;
      }),
    );
  }
  // below codes are for blob files upload to datalake, if azureConnectionString is not set.
  const { fileTokens, checkUploadTokenExpiration, fetchFileTokens } =
    useFileStore.getState();

  let uploadToken: string;
  const isUploadTokenExpired = !fileTokens || checkUploadTokenExpiration('tmp');
  if (isUploadTokenExpired) {
    const newTokens = await fetchFileTokens();
    uploadToken = newTokens.tmp?.sasToken;
  } else {
    uploadToken = fileTokens.tmp?.sasToken;
  }

  const datalakeServiceClient = new DataLakeServiceClient(
    `https://${azureStorageAccountName}.dfs.core.windows.net?${uploadToken}`,
  );
  const fileSystemClient =
    datalakeServiceClient.getFileSystemClient(containerName);
  const directoryClient = fileSystemClient.getDirectoryClient(directoryName);
  try {
    // exists() (a HEAD) instead of createIfNotExists(): the latter issues the
    // create unconditionally and Azure Data Lake returns a 409
    // (PathAlreadyExists) for an existing directory, which surfaces as a
    // confusing error in the Network tab. The catch below still swallows a 409
    // in case of a create/create race.
    if (!(await directoryClient.exists())) {
      await directoryClient.create();
    }
  } catch (error: any) {
    const statusCode = error?.statusCode || error?.response?.status;
    const errorCode =
      error?.code || error?.details?.errorCode || error?.response?.errorCode;
    if (
      statusCode !== 409 &&
      errorCode !== 'PathAlreadyExists' &&
      errorCode !== 'ContainerAlreadyExists' &&
      errorCode !== 'DirectoryAlreadyExists'
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        'File Debug: Re-creating directory - ',
        error?.message || error,
      );
    }
  }

  // Function to upload the file in chunks
  // Function to upload the file in chunks with parallelism and error handling
  // Function to upload the file in chunks with batching of 10 parallel operations
  const uploadInChunks = async (
    fileClient: ReturnType<typeof directoryClient.getFileClient>,
    file: File,
    chunkSize = 15 * 1024 * 1024, // 15 MB
  ): Promise<void> => {
    const fileSize = file.size;
    const reader = file.stream().getReader();

    // Create the file on Azure Blob Storage
    await fileClient.create();

    let offset = 0;
    const appendPromises: Promise<void>[] = [];

    // Function to handle the execution of append operations in batches
    const executeBatch = async () => {
      // Wait for the current batch of append operations to complete
      await Promise.all(appendPromises);
      // Clear the batch for the next set of operations
      appendPromises.length = 0;
    };

    try {
      while (offset < fileSize) {
        const { done, value } = await reader.read();
        if (done || !value) break;

        // Adjust the chunk size to prevent slicing beyond available data
        const chunk = value.slice(0, Math.min(chunkSize, value.length));

        // Push the append operation to the promises array for parallel execution
        appendPromises.push(
          fileClient.append(chunk, offset, chunk.length).then(() => {}),
        );
        offset += chunk.length;

        // If the batch reaches 10 operations, execute the batch
        if (appendPromises.length === 10) {
          await executeBatch();
        }
      }

      // Execute any remaining append operations
      if (appendPromises.length > 0) {
        await executeBatch();
      }

      // Finalize the upload by flushing the entire file size
      await fileClient.flush(fileSize);
    } catch (error) {
      throw new Error('Failed to upload file in chunks');
    }
  };

  // Iterate over each file and upload
  const uploadPromises = files.map(async (fileItem) => {
    const file = fileItem.file;
    if (!file) return fileItem;

    let fileName = generateRandomFileName(file.name);
    if (fileNamePrefix) {
      fileName = `${fileNamePrefix}-` + fileName;
    }
    const fileClient = directoryClient.getFileClient(fileName);

    // Upload the file in chunks
    await uploadInChunks(fileClient, file);
    const { file: _file, ...rest } = fileItem as any;
    return {
      ...rest,
      url: `${azureStorageUrl}/${containerName}/${directoryName}/${fileName}?${uploadToken}`,
    };
  });

  return Promise.all(uploadPromises);
};

export const uploadSignatureFile = async (
  base64String: string,
  fileName = 'signature.png',
): Promise<IFile[]> => {
  const signatureFile = generateFileFromBase64(base64String, fileName);
  const payload: IFile = {
    name: signatureFile.name,
    size: signatureFile.size,
    mimeType: signatureFile.type,
    file: signatureFile,
  };
  const uploadedSignatureFile = await uploadFiles([payload]);
  return uploadedSignatureFile;
};

export const downloadFile = async (
  file: IFile,
  shouldAttachSasToken = true,
): Promise<void> => {
  const { updateToast } = useToastStore.getState();
  try {
    if (!file.url) throw new Error('File URL is undefined.');

    let fileUrl = file.url?.split('?')[0]; // url without token
    if (shouldAttachSasToken) {
      let fileSasToken: string | undefined = file.url?.split('?')[1]; // token
      if (useAzurite !== 'true') {
        fileSasToken = await getSasTokenFromFileUrl(file.url); // get up-to-date valid token

        if (!fileSasToken) throw new Error('SAS token is undefined.');

        fileUrl = getFileUrlWithValidToken(file, fileSasToken);
      } else {
        fileUrl = `${fileUrl}?${fileSasToken}`;
      }
    }

    const res = await fetch(fileUrl);

    if (!res.ok) {
      throw new Error('File download failed.');
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err: any) {
    updateToast({ open: true, message: err.message });
  }
};

export const previewPdf = async (
  file: IFile,
  shouldAttachSasToken = true,
): Promise<void> => {
  const { updateToast } = useToastStore.getState();
  try {
    if (!file.url) throw new Error('File URL is undefined.');

    let fileUrl = file.url?.split('?')[0]; // url without token
    if (shouldAttachSasToken) {
      let fileSasToken: string | undefined = file.url?.split('?')[1]; // token
      if (useAzurite !== 'true') {
        fileSasToken = await getSasTokenFromFileUrl(file.url); // get up-to-date valid token

        if (!fileSasToken) throw new Error('SAS token is undefined.');

        fileUrl = getFileUrlWithValidToken(file, fileSasToken);
      } else {
        fileUrl = `${fileUrl}?${fileSasToken}`;
      }
    }

    const res = await fetch(fileUrl);

    if (!res.ok) {
      throw new Error('File download failed.');
    }

    let blob = await res.blob();
    blob = blob.slice(0, blob.size, 'application/pdf');
    const url = URL.createObjectURL(blob);

    window.open(url, '_blank');
  } catch (err: any) {
    updateToast({ open: true, message: err.message });
  }
};
